// Approvals — the managers' single inbox (showcase UX finding 3,
// 2026-10-08; backend approvals.py, approval_rules.py, routers/approvals.py).
//
// Every decision waiting on the caller, from anywhere in the app, oldest
// first: proposals to review and S&P sign-offs (Briefs), gateway decisions
// and deliverables in review (projects), PM assignment (Delivery board),
// requested variations, tender awards, field captures to review. Filter
// chips by type; each row has one "open" button that lands on the screen
// where that decision is made (switching workspace when it lives in
// another one). The rows come from the My work rule engine, so an item
// clears itself once it is decided.
//
// A tab in every manager workspace (manager+ only), and My work shows a
// one-line link here when anything is waiting.
import { API_MUTATED_EVENT, getJson } from "./api";
import type { ApiMutatedDetail } from "./api";
import { isGisOnly } from "./dataProfile";
import { el } from "./legend";
import { getState, roleAtLeast } from "./state";
import "./styles/approvals.css";
import { showToast } from "./toast";
import { WORKSPACES, currentWorkspace, switchWorkspace, workspaceChoices } from "./workspaces";
import type { WorkspaceKey } from "./workspaces";

interface ApprovalLink {
  type: string;
  id?: string | number;
  tab?: string;
  phase?: string;
}

interface ApprovalItem {
  id: number;
  type: string;
  type_label: string;
  action: string;
  title: string;
  detail: string | null;
  severity: "info" | "warning" | "critical";
  link: ApprovalLink | null;
  since: string | null;
  age_days: number | null;
  project_id: string | null;
}

interface InboxResp {
  items: ApprovalItem[];
  total: number;
  counts: Record<string, number>;
  types: { key: string; label: string; count: number }[];
  /** the server couldn't re-run the rules in time (another evaluation was
   * running): this is the last stored result, and the page retries */
  refreshing?: boolean;
}

let typeFilter = "";
let rendering = 0;
let refreshRetries = 0;
let retryTimer: number | null = null;
const REFRESH_RETRY_MS = 2500;
const MAX_REFRESH_RETRIES = 6;
// Doubt about the rows on screen (take-2 notes, 2026-10-08): after a
// successful change (API_MUTATED_EVENT) the rows on screen, or the stored
// result the server falls back to while another evaluation runs, may still
// hold an item that was just decided (the S&P sign-off beat flashed the old
// "proposal v1" row for ~1 s). While in doubt the page shows "Refreshing…"
// instead of those rows.
//
// Take-3 notes: "Refreshing…" showed twice in ~2 s (refresh, list, refresh,
// list). Two causes: background POSTs that decide nothing (presence pings on
// an open brief, the share poll, marking a notification read) also counted
// as changes, and a tab click plus the debounced re-count each started their
// own refresh. Now only decision-like requests raise doubt, doubt is a pair
// of clocks (latest change vs. the request the rows on screen came from),
// so rows fetched after the change are never swapped back for the
// placeholder, and a render cancels a re-count that is still to come.
let lastChangeAt = 0; // Date.now() of the latest decision-like change
let rowsFreshAsOf = 0; // request start of the freshly evaluated rows on screen
let showingPlaceholder = false;

function rowsDoubtful(): boolean {
  return lastChangeAt > rowsFreshAsOf;
}

function refreshingPage(): HTMLElement {
  const page = el("div", { className: "ap-page" });
  const head = el("div", { className: "ap-head" });
  head.append(el("h2", {}, "Approvals"));
  page.append(head);
  page.append(el("p", { className: "caption ap-refreshing" }, "Refreshing… checking for decisions just made."));
  return page;
}

function container(): HTMLElement | null {
  return document.getElementById("approvals-container");
}

function isActive(): boolean {
  return !!document.getElementById("tab-approvals")?.classList.contains("active");
}

export function canUseApprovals(): boolean {
  // /api/approvals needs a full data profile: a GIS-view organisation's
  // managers get no Approvals tab (workspaces.ts) and the route 404s
  return roleAtLeast(getState().currentUser, "manager") && !isGisOnly();
}

function tabButton(tab: string): HTMLButtonElement | null {
  return document.querySelector<HTMLButtonElement>(`#sidebar .tab-btn[data-tab="${tab}"]`);
}

function tabShown(tab: string): boolean {
  const b = tabButton(tab);
  return !!b && !b.classList.contains("ws-hidden") && b.style.display !== "none";
}

/** Make `tab` reachable: stay if the current workspace shows it, else
 * switch to the first of the user's workspaces that has it (preferring
 * `prefer`). False when none of theirs does. */
export function ensureTab(tab: string, prefer: WorkspaceKey[] = []): boolean {
  if (tabShown(tab)) return true;
  const choices = workspaceChoices(getState().currentUser);
  const candidates = [...prefer, ...choices].filter((w, i, a) => a.indexOf(w) === i && choices.includes(w));
  for (const w of candidates) {
    if (w === "admin" || w === "gis") continue;
    if (!WORKSPACES[w as keyof typeof WORKSPACES]?.tabs.includes(tab)) continue;
    if (switchWorkspace(w)) return tabShown(tab);
  }
  return false;
}

const flowNav = () => {
  document.dispatchEvent(new CustomEvent("wcc-full-app-needed"));
  return import("./flowNav");
};

// Projects open in the full delivery view only outside these (projectsUI.ts SUMMARY_WORKSPACES)
const SUMMARY_WS = new Set<string>(["strategy", "packaging", "admin"]);

function openItem(it: ApprovalItem): void {
  const link = it.link;
  if (!link) return;
  if (it.type === "pm_assignment") {
    if (!ensureTab("deliveryBoard", ["delivery"])) {
      showToast("PM assignment is on the Delivery board, in the Project delivery workspace");
      return;
    }
    tabButton("deliveryBoard")?.click();
    return;
  }
  if (link.type === "project" && link.id) {
    const id = String(link.id);
    if (SUMMARY_WS.has(currentWorkspace()) || !WORKSPACES[currentWorkspace() as keyof typeof WORKSPACES]?.tabs.includes("projects")) {
      ensureTab("projects", ["delivery"]);
    }
    void flowNav()
      .then((m) => m.openProjectById(id, { tab: link.tab ?? null, phase: link.phase ?? null }))
      .catch(() => showToast("Couldn't open that project"));
    return;
  }
  if (link.type === "brief" && link.id) {
    const id = String(link.id);
    if (!ensureTab("briefs", ["strategy", "packaging"])) {
      showToast("Briefs are in the Strategy & monitoring workspace");
      return;
    }
    void flowNav().then((m) => m.openBrief(id));
    return;
  }
  if (link.type === "tab" && link.tab) {
    if (!ensureTab(link.tab, link.tab === "fieldReview" ? ["asset_data"] : [])) {
      showToast("That screen is in a workspace you can't open");
      return;
    }
    tabButton(link.tab)?.click();
  }
}

function age(it: ApprovalItem): string {
  if (it.age_days === null) return "";
  if (it.age_days < 1) return "today";
  const d = Math.floor(it.age_days);
  return d === 1 ? "1 day" : `${d} days`;
}

function row(it: ApprovalItem): HTMLElement {
  const li = el("li", { className: `ap-item ap-${it.severity}` });
  li.dataset.approvalType = it.type;
  li.append(el("div", { className: "ap-type" }, it.type_label));
  const title = el("div", { className: "ap-title" }, it.title);
  li.append(title);
  const open = el("button", { type: "button", className: "ap-open", title: "Open where this decision is made" }, `${it.action} →`);
  open.addEventListener("click", () => openItem(it));
  li.append(open);
  const meta = el("div", { className: "caption" });
  meta.append(`Waiting ${age(it)}${it.since ? ` (since ${new Date(it.since).toLocaleDateString()})` : ""}`);
  if (it.detail) meta.append(` · ${it.detail}`);
  li.append(meta);
  return li;
}

export async function renderApprovals(isRetry = false): Promise<void> {
  if (!isRetry) refreshRetries = 0;
  const root = container();
  if (!root) return;
  // this render fetches now: a debounced re-count still to come would only repeat it
  if (recountTimer !== null) {
    window.clearTimeout(recountTimer);
    recountTimer = null;
  }
  const ticket = ++rendering;
  const startedAt = Date.now();
  if (!root.childElementCount) root.append(el("p", { className: "caption" }, "Loading…"));
  else if (rowsDoubtful() && !showingPlaceholder) {
    root.replaceChildren(refreshingPage());
    showingPlaceholder = true;
  }
  let data: InboxResp;
  try {
    data = await getJson<InboxResp>(`/api/approvals${typeFilter ? `?type=${encodeURIComponent(typeFilter)}` : ""}`);
  } catch (err) {
    if (ticket !== rendering) return;
    showingPlaceholder = false;
    root.replaceChildren(el("p", { className: "caption mw-error" }, err instanceof Error ? err.message : "Couldn't load approvals."));
    return;
  }
  if (ticket !== rendering) return;
  if (!data.refreshing) rowsFreshAsOf = Math.max(rowsFreshAsOf, startedAt);
  // stored (possibly pre-decision) rows while a retry is still to come: hold them back
  const holdRows = !!data.refreshing && rowsDoubtful() && refreshRetries < MAX_REFRESH_RETRIES;
  if (retryTimer !== null) window.clearTimeout(retryTimer);
  retryTimer = null;
  if (data.refreshing && refreshRetries < MAX_REFRESH_RETRIES) {
    refreshRetries += 1;
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      if (isActive()) void renderApprovals(true);
    }, REFRESH_RETRY_MS);
  } else if (!data.refreshing) {
    refreshRetries = 0;
  }
  // a change landed while this request was in flight: its own (debounced)
  // re-count follows, so keep the placeholder up rather than flash these rows
  const changedInFlight = !data.refreshing && rowsDoubtful() && recountTimer !== null;
  if (holdRows || changedInFlight) {
    if (!showingPlaceholder) {
      root.replaceChildren(refreshingPage());
      showingPlaceholder = true;
    }
    return;
  }
  setTabCount(data.total);
  showingPlaceholder = false;
  const page = el("div", { className: "ap-page" });
  const head = el("div", { className: "ap-head" });
  head.append(el("h2", {}, data.total ? `Approvals (${data.total})` : "Approvals"));
  const refresh = el("button", { type: "button", className: "link-button" }, "Refresh");
  refresh.addEventListener("click", () => void renderApprovals());
  head.append(refresh);
  page.append(head);
  page.append(el("p", { className: "caption" }, "Everything waiting for your decision, oldest first. Each item clears itself once it is decided."));
  if (data.refreshing) {
    page.append(el("p", { className: "caption ap-refreshing" }, "Refreshing… another update is running, so this list may be a few seconds old."));
  }
  const chips = el("div", { className: "ap-filters" });
  const chip = (key: string, label: string, n: number) => {
    const b = el("button", { type: "button", className: `ap-chip${typeFilter === key ? " active" : ""}` }, `${label} (${n})`) as HTMLButtonElement;
    b.dataset.type = key || "all";
    b.disabled = !!key && n === 0 && typeFilter !== key;
    b.addEventListener("click", () => {
      typeFilter = key;
      void renderApprovals();
    });
    chips.append(b);
  };
  chip("", "All", data.total);
  for (const t of data.types) chip(t.key, t.label, t.count);
  page.append(chips);
  if (!data.items.length) {
    page.append(el("p", { className: "caption mw-empty" }, typeFilter ? "Nothing of this type is waiting for you." : "Nothing is waiting for your decision."));
  } else {
    const list = el("ul", { className: "ap-list" });
    for (const it of data.items) list.append(row(it));
    page.append(list);
  }
  root.replaceChildren(page);
}

function setTabCount(n: number): void {
  const btn = document.getElementById("approvals-tab-btn");
  if (btn) btn.textContent = n ? `Approvals (${n})` : "Approvals";
}

/** For My work: how many decisions are waiting (0 for non-managers). */
export async function approvalsWaiting(): Promise<number> {
  if (!canUseApprovals()) return 0;
  try {
    const s = await getJson<{ total: number }>("/api/approvals/summary");
    setTabCount(s.total);
    return s.total;
  } catch {
    return 0;
  }
}

/** Open the Approvals tab (from My work's link). */
export function openApprovals(): void {
  if (!ensureTab("approvals")) {
    showToast("Approvals are in the manager workspaces");
    return;
  }
  tabButton("approvals")?.click();
}

// After a decision (re-shoot finding 2, 2026-10-08): decisions are made on
// many screens (gateway checklist, Delivery board, briefs, procurement,
// field review ...), so rather than touch each one, every successful
// mutating /api request made through api.ts (which fires API_MUTATED_EVENT)
// schedules a re-count. The server lifts its rule-evaluation throttle on the
// same signal (main.py _ApprovalsDirty), so the decided item drops out and
// the tab's "(n)" follows without pressing Refresh. (An earlier version
// wrapped window.fetch globally; an explicit event from the one module that
// makes the requests is easier to reason about and can't interfere with
// streaming, uploads or aborts.)
let recountTimer: number | null = null;
const RECOUNT_DEBOUNCE_MS = 700;

function scheduleRecount(): void {
  if (recountTimer !== null) window.clearTimeout(recountTimer);
  recountTimer = window.setTimeout(() => {
    recountTimer = null;
    if (isActive()) void renderApprovals();
    else void approvalsWaiting();
  }, RECOUNT_DEBOUNCE_MS);
}

// Background requests that decide nothing: sign-in / terms, this inbox
// itself, telemetry, presence pings (sent every few seconds while a brief or
// project is open), the share poll, marking notifications read, and My
// work's own re-evaluation.
const NOT_DECISIONS = /\/api\/(auth|terms|approvals|telemetry|presence|shares\/poll|notifications|my-work\/refresh)\b/;

function isDecisionLike(url: string): boolean {
  if (!/\/api\//.test(url)) return false;
  return !NOT_DECISIONS.test(url);
}

let watching = false;

function watchDecisions(): void {
  if (watching) return;
  watching = true;
  document.addEventListener(API_MUTATED_EVENT, (e) => {
    const d = (e as CustomEvent<ApiMutatedDetail>).detail;
    if (d && isDecisionLike(d.url)) {
      lastChangeAt = Date.now();
      scheduleRecount();
    }
  });
}

/** Call once at boot (main.ts), before initWorkspaces. */
export function initApprovals(): void {
  const btn = document.getElementById("approvals-tab-btn");
  if (!btn) return;
  btn.style.display = canUseApprovals() ? "" : "none";
  if (!canUseApprovals()) return;
  watchDecisions();
  btn.addEventListener("click", () => void renderApprovals());
  window.setInterval(() => {
    if (isActive() && document.visibilityState === "visible") void renderApprovals();
  }, 120_000);
}
